import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Header from "../components/Header";
import { consumeStashedNext, safeNext, stashNext } from "../utils/authRedirect";

export default function LoginPage() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [pendingApproval, setPendingApproval] = useState(false);
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // Where to land after signing in: ?next= when we were bounced here from a guarded page,
    // else a destination parked before the Google round-trip, else the default page.
    const nextPath = safeNext(searchParams.get("next"));

    // Handle Google OAuth callback
    useEffect(() => {
        const token = searchParams.get("token");
        const oauthError = searchParams.get("error");
        const pending = searchParams.get("pending");

        if (token) {
            localStorage.setItem("token", token);
            setSearchParams({}, { replace: true });
            // The OAuth callback returns only ?token=, so the destination comes back from
            // sessionStorage where handleGoogleLogin parked it.
            navigate(consumeStashedNext() || "/");
        } else if (pending === "true") {
            setPendingApproval(true);
            setSearchParams({}, { replace: true });
        } else if (oauthError) {
            if (oauthError === "not_approved") {
                setPendingApproval(true);
            } else {
                setError(`Google sign-in failed: ${oauthError}`);
            }
            setSearchParams({}, { replace: true });
        }
    }, [searchParams, setSearchParams, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);

        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/login`, {
                method: "POST",
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
                body: new URLSearchParams({ username: email, password }),
            });

            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                if (res.status === 403 && data.detail === "Account pending approval") {
                    setPendingApproval(true);
                    return;
                }
                throw new Error(data.detail || "Invalid credentials");
            }

            const data = await res.json();
            localStorage.setItem("token", data.access_token);

            navigate(nextPath || "/"); // deep link if we were sent here, else the landing page
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleLogin = () => {
        // Google bounces through our backend and returns with only ?token=, so a query param
        // cannot carry the destination — park it here and read it on the way back.
        stashNext(nextPath);
        window.location.href = `${process.env.REACT_APP_API_URL}/auth/google/login`;
    };

    const containerStyle = {
        minHeight: "100vh",
        backgroundColor: "#f5f5f5",
        display: "flex",
        flexDirection: "column",
    };

    const mainContentStyle = {
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "40px 20px",
    };

    const formContainerStyle = {
        backgroundColor: "white",
        padding: "40px",
        borderRadius: "12px",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
        width: "100%",
        maxWidth: "400px",
    };

    const titleStyle = {
        textAlign: "center",
        marginBottom: "30px",
        fontSize: "28px",
        fontWeight: "600",
        color: "#333",
    };

    const inputStyle = {
        width: "100%",
        padding: "12px 16px",
        margin: "8px 0",
        border: "2px solid #ddd",
        borderRadius: "6px",
        fontSize: "16px",
        boxSizing: "border-box",
        transition: "border-color 0.3s ease",
    };

    const buttonStyle = {
        width: "100%",
        padding: "14px",
        margin: "20px 0 10px 0",
        backgroundColor: "#000",
        color: "white",
        border: "none",
        borderRadius: "6px",
        fontSize: "16px",
        fontWeight: "600",
        cursor: loading ? "not-allowed" : "pointer",
        transition: "background-color 0.3s ease",
        opacity: loading ? 0.7 : 1,
    };

    const errorStyle = {
        color: "#dc3545",
        fontSize: "14px",
        marginTop: "10px",
        textAlign: "center",
    };

    const googleButtonStyle = {
        width: "100%",
        padding: "12px",
        margin: "10px 0",
        backgroundColor: "#fff",
        color: "#333",
        border: "2px solid #ddd",
        borderRadius: "6px",
        fontSize: "16px",
        fontWeight: "500",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "10px",
        transition: "background-color 0.3s ease, border-color 0.3s ease",
    };

    const dividerStyle = {
        display: "flex",
        alignItems: "center",
        margin: "20px 0",
        color: "#999",
    };

    const dividerLineStyle = {
        flex: 1,
        height: "1px",
        backgroundColor: "#ddd",
    };

    const pendingStyle = {
        textAlign: "center",
        padding: "20px",
    };

    // Show pending approval screen
    if (pendingApproval) {
        return (
            <div style={containerStyle}>
                <Header showLogout={false} />
                <div style={mainContentStyle}>
                    <div style={formContainerStyle}>
                        <div style={pendingStyle}>
                            <div style={{ fontSize: "48px", marginBottom: "20px" }}>⏳</div>
                            <h2 style={{ ...titleStyle, marginBottom: "16px" }}>Pending Approval</h2>
                            <p style={{ color: "#666", fontSize: "16px", lineHeight: "1.6" }}>
                                Your account is awaiting approval. You'll be able to sign in once an administrator approves your access.
                            </p>
                            <button
                                onClick={() => setPendingApproval(false)}
                                style={{ ...buttonStyle, backgroundColor: "#666", marginTop: "20px" }}
                            >
                                Back to Sign In
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={containerStyle}>
            <Header showLogout={false} />

            <div style={mainContentStyle}>
                <div style={formContainerStyle}>
                    <h2 style={titleStyle}>Sign In</h2>

                    {/* Google Sign In Button */}
                    <button
                        onClick={handleGoogleLogin}
                        style={googleButtonStyle}
                        onMouseOver={(e) => {
                            e.target.style.backgroundColor = "#f5f5f5";
                            e.target.style.borderColor = "#ccc";
                        }}
                        onMouseOut={(e) => {
                            e.target.style.backgroundColor = "#fff";
                            e.target.style.borderColor = "#ddd";
                        }}
                    >
                        <svg width="18" height="18" viewBox="0 0 18 18">
                            <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
                            <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z"/>
                            <path fill="#FBBC05" d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71s.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
                            <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
                        </svg>
                        Continue with Google
                    </button>

                    <div style={dividerStyle}>
                        <div style={dividerLineStyle}></div>
                        <span style={{ padding: "0 15px", fontSize: "14px" }}>or</span>
                        <div style={dividerLineStyle}></div>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <input
                            type="email"
                            placeholder="Email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            style={inputStyle}
                        />
                        <input
                            type="password"
                            placeholder="Password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            style={inputStyle}
                        />
                        <button
                            type="submit"
                            style={buttonStyle}
                            disabled={loading}
                        >
                            {loading ? "Signing In..." : "Sign In"}
                        </button>
                    </form>
                    {error && <div style={errorStyle}>{error}</div>}
                </div>
            </div>
        </div>
    );
}